import type { ReactElement } from 'react';
import type { BlogRole } from '@gr/core/accounts/workspaces/dto';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { useServices, useSession, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Card } from '../../../ui-kit/Card/Card';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { AddMemberForm } from './AddMemberForm/AddMemberForm';
import { useBlog } from './blog-context';
import { MemberTable } from './MemberTable/MemberTable';

// WS-UC-4, WS-UC-5, WS-UC-5.CC-1, WS-UC-4.CC-1
export function MembersSection(): ReactElement {
  const { api, session } = useServices();
  const { t, te } = useT();
  const { blog, can } = useBlog();
  const { me } = useSession();
  const members = useResource(() => api.workspaces.members(blog.id), [api, blog.id]);
  const manage = can(BlogPermission.ManageMembers);
  const change = useAction((userId: string, next: BlogRole | null) =>
    next ? api.workspaces.updateMember(blog.id, userId, next) : api.workspaces.removeMember(blog.id, userId),
  );

  const apply = async (userId: string, next: BlogRole | null): Promise<void> => {
    const result = await change.run(userId, next);
    if (result.success) {
      members.setData(result.data);
      if (userId === me?.user.id) await session.refresh();
    }
  };

  return (
    <div className="u-stack-lg">
      <Card title={t('workspaces.members.title')}>
        <div className="u-stack">
          <p className="u-muted u-small">{t('workspaces.members.permissions')}</p>
          {change.error ? <Alert tone="danger">{te(change.error)}</Alert> : null}
          {members.loading && !members.data ? (
            <Loading label={t('common.loading')} />
          ) : members.error ? (
            <Alert tone="danger">{te(members.error)}</Alert>
          ) : (
            <MemberTable
              members={members.data ?? []}
              manage={manage}
              pending={change.pending}
              onRoleChange={(member, role) => void apply(member.userId, role)}
              onRemove={(member) => void apply(member.userId, null)}
            />
          )}
        </div>
      </Card>
      {manage ? (
        <Card title={t('workspaces.members.add')}>
          <AddMemberForm
            onSubmit={async (values) => {
              const result = await api.workspaces.addMember(blog.id, values.login, values.role);
              if (result.success) members.setData(result.data);
              return result;
            }}
          />
        </Card>
      ) : null}
    </div>
  );
}
